import React, { useState } from 'react';
import { Button } from 'primereact/button'; 
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { FloatLabel } from 'primereact/floatlabel';
import { Row, Col } from 'react-bootstrap';
import { fetchBusinessSuggestions } from '../../services/api'; // API hívás
import { Card } from 'primereact/card'; // Importáljuk a Card komponenst

interface Location {
  name: string;
  address: string;
}

interface LocationDetailsProps {
  franchiseName: string;
  setFranchiseName: React.Dispatch<React.SetStateAction<string>>;
  locations: Location[];
  setLocations: React.Dispatch<React.SetStateAction<Location[]>>;
  nextStep: () => void;
  prevStep: () => void;
}

const LocationDetails: React.FC<LocationDetailsProps> = ({
  franchiseName,
  setFranchiseName,
  locations,
  setLocations,
}) => {

  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]); // API válaszok tárolása
  const [locationFields, setLocationFields] = useState<{ name: string; address: string }[]>([]);

  // Helyszín hozzáadás kezelése
  const handleAddLocation = () => {
    console.log('Add Location gomb megnyomva');
    // Dinamikusan hozzáadunk egy üres location-t
    setLocationFields([...locationFields, { name: '', address: '' }]);
  };

  // Autocomplete címek API kérés
  const handleAddressAutocomplete = async (name: string) => {
    console.log('Autocomplete cím keresése:', name);
    if (name.length >= 4) {
      const suggestions = await fetchBusinessSuggestions(name); // API hívás
      console.log('Autocomplete válasz:', suggestions);
      if (suggestions.length > 0) {
        setAddressSuggestions(suggestions); // Megjeleníti a válaszokat
      }
    }
  };

  // Helyszín mentése
  const handleSaveLocation = (index: number) => {
    if (locationFields[index].name && locationFields[index].address) {
      setLocations((prevLocations) => [
        ...prevLocations,
        locationFields[index], // Új helyszín hozzáadása a locations tömbhöz
      ]);
      setLocationFields(locationFields.filter((_, i) => i !== index)); // Elvetjük a kitöltött location-t a locationFields-ből
    }
  };

  // Helyszín törlése a már mentett location listából
  const handleDeleteSavedLocation = (index: number) => {
    const updatedLocations = locations.filter((_, i) => i !== index); // Az adott helyszín törlése
    setLocations(updatedLocations);
    console.log(`Location ${index + 1} törölve`);
  };

  return (
    <>
      <Row className='mt-3 mb-2'>
        <Col xs={12} sm={12} md={12} lg={12}>
          {/* Franchise name */}
          <FloatLabel>
            <IconField iconPosition="left">
                <InputIcon className="pi pi-briefcase"> </InputIcon>
                <InputText
                  id="franchise-name"
                  value={franchiseName}
                  onChange={(e) => setFranchiseName(e.target.value)}
                  placeholder="Enter franchise name"
                  className='w-full'
                  autoFocus
                />
            </IconField>
            <label htmlFor="franchise-name">Franchise Name</label>
          </FloatLabel>
        </Col>
      </Row>

      {/* Locations list (mentett helyszínek listája) */}
      <Row>
        {locations.map((location, index) => (
          <Col key={index} xs={12} sm={5} md={6} className="mb-4">
            <Card className="p-mt-3">
              <h3 className="mb-5 mt-0">Location {index + 1}</h3>

              <FloatLabel className="mb-4">
                <InputText
                  id={`location-name-${index}`}
                  value={location.name}
                  onChange={(e) => {
                    const updatedLocations = [...locations];
                    updatedLocations[index].name = e.target.value;
                    setLocations(updatedLocations); // Állapot frissítése
                  }}
                  placeholder="Location name"
                />
                <label htmlFor={`location-name-${index}`}>Location Name</label>
              </FloatLabel>

              <FloatLabel>
                <InputText
                  id={`location-address-${index}`}
                  value={location.address}
                  placeholder="Address will be auto-filled"
                />
                <label htmlFor={`location-address-${index}`}>Location Address</label>
              </FloatLabel>

              {/* Törlés gomb (csak a már mentett helyszíneknél) */}
              <Button
                label="Delete Location"
                icon="pi pi-trash"
                className="p-button-danger p-mt-3 mt-3 pull-right"
                onClick={() => handleDeleteSavedLocation(index)} // Helyszín törlése
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Dynamically added input fields for new locations */}
      {locationFields.map((_, index) => (
        <Row className="p-mt-3" key={index}>
          <Col>
            <InputText
              value={locationFields[index].name}
              onChange={(e) => {
                const updatedFields = [...locationFields];
                updatedFields[index].name = e.target.value;
                setLocationFields(updatedFields);
              }}
              placeholder="Enter location name"
              onBlur={() => handleAddressAutocomplete(locationFields[index].name)} // Autocomplete address when leaving the input field
            />
          </Col>
          <Col>
            <InputText
              value={locationFields[index].address}
              onChange={(e) => {
                const updatedFields = [...locationFields];
                updatedFields[index].address = e.target.value;
                setLocationFields(updatedFields);
              }}
              placeholder="Location address"
            />
          </Col>
          <Col>
            <Button
              label="Save Location"
              icon="pi pi-save"
              className="p-button-success p-mt-3"
              onClick={() => handleSaveLocation(index)} // Mentés gomb, elmenti a location-t
            />
          </Col>
        </Row>
      ))}

      {/* Add new location button */}
      <Row>
        <Col>
          <Button
            label="Add Location"
            icon="pi pi-plus"
            className="p-button-success p-mt-3"
            onClick={handleAddLocation} // Dinamikusan hozzáadja a mezőket
          />
        </Col>
      </Row>

      {/* Autocomplete dropdown */}
      {addressSuggestions.length > 0 && (
        <Row>
          <Col>
            <div className="address-suggestions">
              <ul>
                {addressSuggestions.map((suggestion, index) => (
                  <li
                    key={index}
                    onClick={() => {
                      const updatedFields = [...locationFields];
                      updatedFields[updatedFields.length - 1].address = suggestion.address;
                      setLocationFields(updatedFields);
                      setAddressSuggestions([]); // Törli a lista megjelenítését
                    }}
                  >
                    <strong>{suggestion.business_name}</strong> <small>{suggestion.address}</small>
                  </li>
                ))}
              </ul>
            </div>
          </Col>
        </Row>
      )}
    </>
  );
};

export default LocationDetails;
